/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useMemo } from 'react';
import {
	BUTTON,
	NAVIGATION_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM_BUTTON,
	CUSTOM_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { MEATBALL_DROPDOWN } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-id.tsx';
import { LEVEL_TWO } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	UseAnalyticsL2,
	ViewParams,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/controllers/analytics/types.tsx';
import { ROADMAP } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/item-type.tsx';
import type { NavigationCollection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/common/types.tsx';

export const useRoadmapsAnalyticsL2: UseAnalyticsL2<NavigationCollection> = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const commonAttributes = useMemo(
		() => ({
			level: LEVEL_TWO,
		}),
		[],
	);

	// meatball menu on roadmaps
	const sendMeatBallDropDownOnProjectsAnalyticsEvent = useCallback(
		(container: NavigationCollection) => {
			const { collectionUUID } = container;

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: BUTTON,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: MEATBALL_DROPDOWN,
				parentItemId: collectionUUID,
				parentItemType: ROADMAP,
			};
			fireUIAnalytics(event, 'roadmapsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// view navigation item under each roadmap
	const sendViewNavigationItemInsideProjectsAnalyticsEvent = useCallback(
		({ container, viewParams }: { container: NavigationCollection; viewParams: ViewParams }) => {
			const { collectionUUID } = container;

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM,
				itemId: viewParams.viewId,
				itemType: viewParams.viewKind,
				parentItemId: collectionUUID,
				parentItemType: ROADMAP,
			};
			fireUIAnalytics(event, 'roadmapsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// create view button on view navigation item
	const sendCreateViewOnViewNavigationItemAnalyticsEvent = useCallback(
		(container: NavigationCollection) => {
			const { collectionUUID } = container;

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: 'createView',
				parentItemId: collectionUUID,
				parentItemType: ROADMAP,
			};
			fireUIAnalytics(event, 'roadmapsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// create view button on roadmaps menu
	const sendCreateViewOnProjectMenuItemAnalyticsEvent = useCallback(
		(container: NavigationCollection) => {
			const { collectionUUID } = container;

			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: BUTTON,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: 'createView',
				parentItemId: collectionUUID,
				parentItemType: ROADMAP,
			};
			fireUIAnalytics(event, 'roadmapsNavigationMenuItem', attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	return {
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
		sendViewNavigationItemInsideProjectsAnalyticsEvent,
		sendCreateViewOnViewNavigationItemAnalyticsEvent,
		sendCreateViewOnProjectMenuItemAnalyticsEvent,
	};
};
